/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { JumpToViewWord } from '@components/editors/jump-to-view-word.tsx';
import cn from 'classnames';
import { AudioWaveform } from '@components/editors/audio-waveform.tsx';
import { getCurrentSegmentSeeking } from '@utils/editors/get-current-segment-seeking.ts';
import { indexOf } from 'lodash';
import {
  Components as VirtuosoComponents,
  Virtuoso,
  VirtuosoHandle
} from 'react-virtuoso';
import { useAudio } from '@utils/providers/audio-context.tsx';

import { SharedCaptionEditorProvider } from '@utils/providers/shared-caption-editor-context.tsx';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import { useInterval } from '@utils/hooks/use-interval.tsx';
import SharedCaptionAbout from '@components/editors/shared-editor/shared-caption/shared-caption-about.tsx';
import { SharedCaptionMediaPlayer } from '@components/editors/shared-editor/shared-caption/shared-caption-media-player.tsx';
import SharedCaptionSegment from '@components/editors/shared-editor/shared-caption/shared-caption-segment.tsx';
import { SharedCaptionToolbar } from '@components/editors/shared-editor/shared-caption/shared-caption-toolbar.tsx';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { usePrompt } from '@components/editors/confirm/prompt.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

type Props = {
  isDocumentFinished: boolean;
};

export default function SharedCaptionMain({ isDocumentFinished }: Props) {
  const { getColour } = useThemeContext();
  const { currentTime, linkedPlayback } = useAudio();
  const currentWordRef = useRef<HTMLSpanElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [matches, setMatches] = useState<DocumentSubtitleSegmentV1[]>([]);

  const {
    document,
    cleanup,
    query,
    search,
    session,
    autoSave,
    isDirty,
    undo,
    redo,
    documentVersion
  } = useSharedCaption();
  useInterval(autoSave, 120 * 1000);

  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName?.toLowerCase();
      const isFormField =
        tag === 'input' ||
        tag === 'textarea' ||
        (el as HTMLElement).isContentEditable;
      return isFormField;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (!ctrlOrMeta) return;
      if (isEditableTarget(e.target)) return; // let Slate / inputs handle it

      const k = e.key.toLowerCase();

      // Ctrl/Cmd + Z → undo
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }

      // Ctrl/Cmd + Shift + Z OR Ctrl/Cmd + Y → redo
      if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [undo, redo]);

  const items = useMemo(() => {
    return (
      document?.document?.filter(item =>
        item.text.toLowerCase().includes(query.toLowerCase())
      ) || []
    );
  }, [document?.document, query]);

  const [showJumpButton, setShowJumpButton] = useState<'up' | 'down' | false>(
    false
  );
  const visibleRangeRef = useRef<{ startIndex: number; endIndex: number }>({
    startIndex: 0,
    endIndex: 0
  });

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  usePrompt({
    isDirty: isDirty
  });

  const onWordOutOfView = (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => {
    currentWordRef.current = ref;
    setShowJumpButton(direction);
  };

  const onWordInView = () => {
    setShowJumpButton(false);
  };

  const jumpToCurrentWord = useCallback(() => {
    const item = getCurrentSegmentSeeking(items, currentTime);
    if (item && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: indexOf(items, item),
        align: 'center',
        behavior: 'smooth'
      });
    }
    setShowJumpButton(false);
  }, [items, currentTime]);

  // Search is active when the user has typed anything into the search box.
  // Trim so pure-whitespace queries don't hide the whole document.
  const isSearchActive = Boolean(search?.trim());

  const filteredItems = useMemo(() => {
    // No search typed → show the full document.
    if (!isSearchActive) return items;
    // Search typed → filter to matches, even if that means zero results.
    // Previously we returned the full list on zero matches, which made the
    // search look broken on files where the term wasn't present (#2107).
    const matchIds = new Set(matches.map(m => m.id));
    return items.filter(item => matchIds.has(item.id));
  }, [isSearchActive, matches, items]);

  // Range-based fallback: when the current segment is virtualized out of the
  // DOM, the per-segment IntersectionObserver can't fire, so derive the jump
  // button state from Virtuoso's visible range and the current segment index.
  useEffect(() => {
    if (!linkedPlayback) {
      setShowJumpButton(false);
      return;
    }
    const current = getCurrentSegmentSeeking(filteredItems, currentTime);
    if (!current) return;
    const idx = indexOf(filteredItems, current);
    if (idx < 0) return;
    const { startIndex, endIndex } = visibleRangeRef.current;
    if (idx < startIndex) setShowJumpButton('up');
    else if (idx > endIndex) setShowJumpButton('down');
  }, [currentTime, filteredItems, linkedPlayback]);

  const renderRow = useCallback(
    (index: number, segment: DocumentSubtitleSegmentV1) => {
      if (!segment) return null;

      return (
        <SharedCaptionSegment
          key={`v${documentVersion}-${segment.start}-${segment.stop}-${segment.id}`}
          index={index}
          segment={segment}
          onWordOutOfView={onWordOutOfView}
          onWordInView={onWordInView}
          filteredItems={filteredItems}
        />
      );
    },
    [filteredItems, onWordOutOfView, onWordInView, documentVersion]
  );

  const vComponents: VirtuosoComponents<DocumentSubtitleSegmentV1, unknown> =
    useMemo(
      () => ({
        Item: ({ children, ...props }) => <div {...props}>{children}</div>
      }),
      []
    );

  const hasWave = !!session?.wave_info_url;

  const { vars, cls } = useThemeVars();

  return (
    <SharedCaptionEditorProvider>
      <div
        className={cn(
          'flex flex-col h-screen overflow-y-auto lg:overflow-y-hidden',
          cls.pageBg,
          cls.text
        )}
        style={vars}
      >
        <SharedCaptionAbout />
        {/* Same flex-based fix as the private caption editor: fill the space
            between the top About bar and the (optional) waveform, so a
            session without ``wave_info_url`` doesn't leave a blank strip. */}
        <div
          className={cn(
            'flex-1 min-h-0 overflow-hidden md:overflow-y-auto px-4'
          )}
        >
          {document && (
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 h-full">
              <div
                className={cn(
                  'relative flex flex-col min-h-0 m-2 rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]',
                  cls.border
                )}
                style={{
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <SharedCaptionToolbar
                  setMatches={setMatches}
                  isDocumentFinished={isDocumentFinished}
                />
                <div
                  role="list"
                  className="overflow-y-auto h-full mb-0.5 min-h-0"
                  style={{
                    minHeight: '200px'
                  }}
                >
                  {filteredItems.length === 0 ? (
                    <div className="flex items-center justify-center h-full p-8">
                      <div className={cn('text-center opacity-60', cls.text)}>
                        <p className="text-sm">
                          {isSearchActive
                            ? `No segments match "${search}"`
                            : 'No segments available'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <Virtuoso
                      key={`virtuoso-${filteredItems.length}`}
                      data={filteredItems}
                      ref={virtuosoRef}
                      totalCount={filteredItems.length}
                      style={{
                        height: '100%',
                        width: '100%',
                        overflowX: 'hidden',
                        minHeight: '34vh'
                      }}
                      components={vComponents}
                      itemContent={renderRow}
                      // Cap initial synchronous mount — each row is a full Slate
                      // editor, so passing the whole length mounted every editor
                      // at once and OOM-killed mobile Safari on open. A small
                      // window virtualizes the rest. (See caption-main.tsx.)
                      initialItemCount={Math.min(filteredItems.length, 20)}
                      increaseViewportBy={200}
                      defaultItemHeight={91}
                      overscan={20}
                      rangeChanged={range => {
                        visibleRangeRef.current = range;
                      }}
                    />
                  )}
                </div>

                <JumpToViewWord
                  visible={showJumpButton}
                  onJumpToWord={jumpToCurrentWord}
                  height={session?.wave_info_url ? 140 : 54}
                />
              </div>

              <div className="relative flex flex-col min-h-0 p-2">
                <SharedCaptionMediaPlayer />
              </div>
            </div>
          )}
        </div>

        {hasWave && (
          <div
            className={cn(
              'w-full overflow-hidden px-3 shadow-sm border',
              cls.cardBg,
              cls.border
            )}
          >
            <AudioWaveform isCaptionEditor={true} session={session} />
          </div>
        )}
      </div>
    </SharedCaptionEditorProvider>
  );
}
